"use client";

import {
  Button,
  Calendar,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { TimeField } from "@/components/ui/FormFields/TimeFormField/TimeField";
import { useForwardedRef } from "@/hooks/useForwardedRef";
import { cn } from "@/lib/utils/cn";
import { parseDate, parseDateTime } from "@internationalized/date";
import dayjs from "dayjs";
import { CalendarIcon, XIcon } from "lucide-react";
import React, { useRef, useState } from "react";
import {
  type DateValue,
  useButton,
  useDatePicker,
  useInteractOutside,
} from "react-aria";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";
import { type DatePickerStateOptions, useDatePickerState } from "react-stately";
import { DateField } from "./DateField";

const DateFormField = React.forwardRef<
  HTMLDivElement,
  DatePickerStateOptions<DateValue> & {
    name: string;
    register: UseFormRegisterReturn;
    label?: string;
    clearable?: boolean;
    suggestedDate?: Date;
    suggestedDateText?: string;
    /** Local calendar day; maps to react-stately `minValue` (cannot select earlier dates). */
    minDate?: Date;
    groupClassName?: string;
    fieldClassName?: string;
  }
>(
  (
    {
      clearable = true,
      name,
      suggestedDate,
      suggestedDateText,
      minDate,
      groupClassName,
      fieldClassName,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useForwardedRef(forwardedRef);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const outsidePointerTargetRef = useRef<HTMLElement | null>(null);
    const closedByOutsidePointerRef = useRef(false);

    const isInDisabledFieldset = Boolean(
      buttonRef.current?.closest("fieldset:disabled"),
    );

    const form = useFormContext();
    const [open, setOpen] = useState(false);

    const resolvedMinValue =
      minDate != null
        ? parseDate(dayjs(minDate).startOf("day").format("YYYY-MM-DD"))
        : props.minValue;

    const state = useDatePickerState({
      ...props,
      minValue: resolvedMinValue,
      defaultValue: form.getValues(name)
        ? ["minute", "hour", "second"].includes(props.granularity ?? "")
          ? parseDateTime(form.getValues(name) as string)
          : parseDate(
              dayjs(form.getValues(name) as string).format("YYYY-MM-DD"),
            )
        : props.value,
    });
    const {
      groupProps,
      fieldProps,
      buttonProps: _buttonProps,
      dialogProps,
      calendarProps,
    } = useDatePicker({ ...props, minValue: resolvedMinValue }, state, ref);
    const { buttonProps } = useButton(_buttonProps, buttonRef);
    useInteractOutside({
      ref: contentRef,
      isDisabled: !open,
      onInteractOutsideStart: (event) => {
        closedByOutsidePointerRef.current = true;
        outsidePointerTargetRef.current =
          event.target instanceof HTMLElement ? event.target : null;
      },
      onInteractOutside: () => {
        setOpen(false);
      },
    });

    return (
      <FormField
        control={form.control}
        disabled={isInDisabledFieldset}
        {...form.register(name)}
        // @ts-expect-error This line is to remove a console error
        ref={null}
        render={({ field }) => (
          <FormItem>
            <div className="flex">
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormMessage
                data-testid="error-message"
                className="grow text-right"
              />
            </div>
            <FormControl>
              <div
                {...groupProps}
                ref={ref}
                className={cn(
                  groupProps.className,
                  "ring-offset-background focus-within:ring-ring relative flex items-center rounded-md focus-within:ring-2 focus-within:ring-offset-2",
                  groupClassName,
                )}
              >
                <DateField
                  {...fieldProps}
                  className={fieldClassName}
                  isDisabled={fieldProps.isDisabled || isInDisabledFieldset}
                  onChange={(value) => {
                    fieldProps.onChange?.(value);
                    field.onChange(value?.toString());
                  }}
                  dataTestId={`${name}-input`}
                />
                {field.value && clearable && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-12 mx-2 my-1 h-4 w-4 cursor-pointer"
                      onClick={(event) => {
                        event.stopPropagation();
                        fieldProps.onChange?.(null);
                        field.onChange("");
                      }}
                      type="button"
                    >
                      <XIcon
                        className={cn(
                          "text-muted-foreground absolute mx-2 my-1 h-3 w-3 cursor-pointer",
                        )}
                      />
                    </Button>
                  </>
                )}
                <Popover
                  modal
                  open={open}
                  onOpenChange={(nextOpen) => {
                    if (nextOpen) {
                      closedByOutsidePointerRef.current = false;
                      outsidePointerTargetRef.current = null;
                    }
                    setOpen(nextOpen);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      {...buttonProps}
                      variant="outline"
                      size="icon"
                      className="border-input h-10 shrink-0 self-stretch rounded-l-none"
                      ref={buttonRef}
                      disabled={buttonProps.disabled || isInDisabledFieldset}
                      onClick={() => {
                        setOpen(true);
                      }}
                      data-testid={`${name}-calendar-trigger`}
                    >
                      <CalendarIcon className="h-5 w-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    ref={contentRef}
                    className="w-full"
                    onPointerDownOutside={(event) => {
                      closedByOutsidePointerRef.current = true;
                      const target = event.detail.originalEvent.target;
                      outsidePointerTargetRef.current =
                        target instanceof HTMLElement ? target : null;
                    }}
                    onCloseAutoFocus={(event) => {
                      const focusedElement = document.activeElement;
                      const focusedEditableOutsideCalendar =
                        focusedElement instanceof HTMLElement &&
                        !contentRef.current?.contains(focusedElement) &&
                        focusedElement.matches(
                          'input, textarea, select, [contenteditable="true"]',
                        );
                      if (
                        !closedByOutsidePointerRef.current &&
                        !focusedEditableOutsideCalendar
                      ) {
                        return;
                      }
                      event.preventDefault();
                      const target = outsidePointerTargetRef.current;
                      closedByOutsidePointerRef.current = false;
                      outsidePointerTargetRef.current = null;
                      if (
                        target?.isConnected &&
                        target.matches(
                          'input, textarea, select, [contenteditable="true"]',
                        )
                      ) {
                        target.focus({ preventScroll: true });
                      }
                    }}
                  >
                    <div {...dialogProps} className="space-y-3">
                      <Calendar
                        {...calendarProps}
                        suggestedDate={suggestedDate}
                        suggestedDateText={suggestedDateText}
                        isDisabled={
                          calendarProps.isDisabled || isInDisabledFieldset
                        }
                        onChange={(value) => {
                          calendarProps.onChange?.(value);
                          field.onChange(value.toString());

                          if (!state.hasTime) {
                            setOpen(false);
                          }
                        }}
                        dataTestIdPrefix={name}
                      />
                      {!!state.hasTime && (
                        <TimeField
                          value={state.timeValue}
                          isDisabled={isInDisabledFieldset}
                          onChange={(value) => {
                            if (value === null) return;
                            state.setTimeValue(value);
                            field.onChange(value.toString());
                          }}
                        />
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    );
  },
);

DateFormField.displayName = "DateFormField";

export { DateFormField };
